import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
} from './ui/alert-dialog';
import OfficeModalHeader from './office/OfficeModalHeader';
import { cn } from '../lib/utils';

const ConfirmDialogContext = createContext(null);

/**
 * Substitui window.confirm por um pop-up no visual do Office.
 * Uso: const ok = await confirm({ title, description, confirmLabel, cancelLabel, tone })
 *
 * Importante: o resultado só é resolvido em onOpenChange(false), com um flag
 * marcado pelo botão Confirmar. Evita a corrida Radix em que onOpenChange
 * cancelava o confirm antes do onClick (exclusão / ações pareciam "não fazer nada").
 */
export function ConfirmDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({
    title: 'Confirmar',
    description: '',
    confirmLabel: 'Confirmar',
    cancelLabel: 'Cancelar',
    tone: 'default',
  });
  const resolverRef = useRef(null);
  const confirmedRef = useRef(false);

  const confirm = useCallback((options = {}) => {
    const {
      title = 'Confirmar',
      description = '',
      confirmLabel = 'Confirmar',
      cancelLabel = 'Cancelar',
      tone = 'default',
    } = typeof options === 'string' ? { description: options } : options || {};

    return new Promise((resolve) => {
      if (resolverRef.current) {
        resolverRef.current(false);
      }
      confirmedRef.current = false;
      resolverRef.current = resolve;
      setOpts({ title, description, confirmLabel, cancelLabel, tone });
      setOpen(true);
    });
  }, []);

  const settleClose = useCallback(() => {
    const resolve = resolverRef.current;
    if (!resolve) {
      setOpen(false);
      return;
    }
    const ok = confirmedRef.current;
    confirmedRef.current = false;
    resolverRef.current = null;
    setOpen(false);
    resolve(Boolean(ok));
  }, []);

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) settleClose();
        }}
      >
        <AlertDialogContent
          className="office-feedback-dialog border-border bg-card text-foreground"
          data-testid="office-confirm-dialog"
        >
          <OfficeModalHeader
            asAlert
            layout="row"
            variant="circle"
            icon={opts.tone === 'destructive' ? AlertTriangle : HelpCircle}
            title={opts.title}
            description={opts.description || undefined}
          />
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                confirmedRef.current = false;
              }}
              data-testid="office-confirm-cancel"
            >
              {opts.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                opts.tone === 'destructive' &&
                  'bg-destructive text-destructive-foreground hover:bg-destructive/90',
              )}
              onClick={() => {
                confirmedRef.current = true;
              }}
              data-testid="office-confirm-ok"
            >
              {opts.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error('useConfirm deve ser usado dentro de ConfirmDialogProvider');
  }
  return ctx;
}

export default ConfirmDialogProvider;
